export class CookieConsentManager {
  #dialogClosed = Promise.withResolvers();

  #gradeLevel = '';

  #userRole = '';

  // #region getters/setters
  /** @type {?boolean} */
  static get acceptsAnalyticsCookies() {
    const value = localStorage.getItem('accepts-analytics-cookies');
    return value === null ? value : value === 'yes';
  }

  /** @param {boolean} value */
  static set acceptsAnalyticsCookies(value) {
    localStorage.setItem('accepts-analytics-cookies', value ? 'yes' : 'no');
  }

  /** @type {?string} */
  get gradeLevel() {
    return this.#gradeLevel;
  }

  /** @param {string} value */
  set gradeLevel(value) {
    this.#gradeLevel = value;
  }

  /** @type {?boolean} */
  static get selfIdComplete() {
    return localStorage.getItem('self-id-complete');
  }

  /** @param {boolean} value */
  static set selfIdComplete(value) {
    localStorage.setItem('self-id-complete', value ? 'yes' : 'no');
  }

  /** @type {?string} */
  get userRole() {
    return this.#userRole;
  }

  /** @param {string} value */
  set userRole(value) {
    this.#userRole = value;
  }
  // #endregion

  // #region private methods
  /**
   * Kicks off the iOS App Tracking interface.
   * @param {import('@common/components/vst-ui-user-role-selection-dialog/vst-ui-user-role-selection-dialog.js').VstUiUserRoleSelectionDialog} userRoleDialog
   */
  async #iOSAppTracking(userRoleDialog) {
    const { impacTracking } = window.plugins;

    const successAction = async () => {
      userRoleDialog.showUserRoleSelectionSubmitButton = true;
      await this.#showUserRoleDialog(userRoleDialog);
      CookieConsentManager.acceptsAnalyticsCookies = true;
    };

    impacTracking.trackingAvailable(avail => {
      if (avail) {
        successAction();
        return;
      }
      impacTracking.canRequestTracking(canRequest => {
        if (canRequest) {
          impacTracking.requestTracking(
            null,
            canTrack => {
              if (canTrack) successAction();
              else {
                console.warn('User does not want tracking');
                CookieConsentManager.acceptsAnalyticsCookies = false;
              }
            },
            error => console.error('Asking for track got error:', error),
          );
        } else console.warn('This platform does not support app tracking.');
      });
    });
  }

  /**
   * @param {import('@common/components/vst-ui-user-role-selection-dialog/vst-ui-user-role-selection-dialog.js').VstUiUserRoleSelectionDialog} userRoleDialog
   */
  async #showUserRoleDialog(userRoleDialog) {
    userRoleDialog.showUserRoleSelectionDialog = true;
    await this.#dialogClosed.promise;
  }
  // #endregion

  // #region public methods
  /**
   * @param {import('@common/components/vst-ui-user-role-selection-dialog/vst-ui-user-role-selection-dialog.js').VstUiUserRoleSelectionDialog} userRoleDialog
   */
  async getCookieConsent(userRoleDialog) {
    if (['GA'].includes(APP_ID) && PLATFORM_VARIANT === 'abitti') {
      return;
    }

    const needsCookieConsent = CookieConsentManager.acceptsAnalyticsCookies === null;
    const needsSelfId =
      CookieConsentManager.acceptsAnalyticsCookies && CookieConsentManager.selfIdComplete === null;

    if (needsCookieConsent || needsSelfId) {
      await import('@common/components/vst-ui-user-role-selection-dialog/vst-ui-user-role-selection-dialog.js');

      if (!['VA'].includes(APP_ID) && PLATFORM_ID === 'ios') {
        this.#iOSAppTracking(userRoleDialog);
        return;
      }

      await this.#showUserRoleDialog(userRoleDialog);
    }
  }

  handleUserRoleDialogClose() {
    this.#dialogClosed.resolve();
  }

  /**
   * @param {Object} detail
   * @param {string} detail.userRole
   * @param {string} detail.gradeLevel
   */
  handleSelfIdComplete({ detail: { userRole, gradeLevel } }) {
    CookieConsentManager.acceptsAnalyticsCookies = true;
    CookieConsentManager.selfIdComplete = true;
    this.#userRole = userRole;
    this.#gradeLevel = gradeLevel;
  }
  // #endregion
}

export const cookieConsentManager = new CookieConsentManager();
